import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Accordion from '../../components/Accordion/Accordion';
import Card from '../../components/Card/Card';
import { Typography, Box } from '@mui/material';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Section from '../../components/Section/Section';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import Button from '../../components/Button/Button';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="General Genomics’ Approach: Excellence in Innovation"
        description="Discover General Genomics’ commitment to data governance, compliance, and innovation. Explore capabilities that drive operational success with clear insights and actionable links."
      />
      <Hero
        helper="Our Approach"
        headline="How we deliver compliant, cutting-edge data solutions"
      />

      <Section theme="innovation" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Discover Solutions Tailored for You
        </h2>
        <p className="action-on-view action-fade-in">
          Unlock the full potential of your organization with General Genomics’
          groundbreaking solutions. Contact us today to see how our advanced
          capabilities in compliance, data governance, and innovation can solve
          your unique challenges. With our proven expertise and commitment to
          operational success, we deliver tools and strategies that empower you
          to excel. Get personalized insights into our offerings and explore the
          tangible benefits of partnering with us. The future of innovation is
          here—experience it firsthand.
        </p>
        <Image
          path="/images/library/office-startup-6.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/command-center-group-3.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Why Choose Us</p>
        <h2 className="action-on-view action-fade-in">
          Experience Tailored Innovation
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Expertise:</strong> Backed by years of experience, we
            deliver solutions that consistently exceed expectations.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Advanced Compliance:</strong> Navigate complex regulations
            with ease through our structured compliance frameworks and expert
            guidance.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Data-Driven Insights:</strong> Harness the power of
            synthetic data (SYN46™) and real-time analytics to fuel
            decision-making.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Client-Centric Approach:</strong> Tailored solutions to
            address your specific operational challenges.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Future-Focused Innovation:</strong> Stay ahead with leading
            edge R&D and scalable technology.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional pages that highlight the power of General Genomics
          in various sectors and how we can support your needs.
        </p>
        <div className="card-group">
          <Card
            icon="account_balance"
            headline="Data Governance & Compliance"
            subhead="Learn how we ensure data governance and compliance, with real-world applications in industries like government and public health."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="science"
            headline="Innovation Research & Development"
            subhead="Explore our cutting-edge research, including projects like Curo46™ and Synthetic Data (SYN46™), pushing the boundaries of genomics."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Our Story & Careers"
            subhead="Understand our mission and values, and explore career opportunities to join our dedicated team."
            btnURL="/about-us"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="devices"
            headline="Connect With Us"
            subhead="Reach out to explore how our AI/ML solutions can address your unique challenges and drive innovation in your industry."
            btnURL="/contact-us"
            btnLabel="Contact us"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/soldiers3.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics’ capabilities
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics excels in innovation, compliance, and
          client-focused solutions to support operational success.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What services does General Genomics offer to support client operations?"
          >
            General Genomics provides expertise in{' '}
            <Link to="/our-approach/data-governance-and-compliance">
              data governance, compliance
            </Link>
            , and{' '}
            <Link to="/our-approach/innovation-research-and-development">
              innovative research and development
            </Link>{' '}
            to enhance operational success and meet client goals.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure data governance and compliance?"
          >
            We implement robust compliance frameworks and governance practices
            tailored to industry standards, supported by well-defined processes
            and visual aids for better understanding.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What innovations set General Genomics apart in the industry?"
          >
            Our Innovation Research & Development team focuses on pioneering
            technologies like SYN46™ and advanced analytics to drive client
            success. We lead the industry with cutting-edge intellectual
            property, including a patented method for predicting susceptibility
            to medical conditions using machine learning models. Our
            forward-looking approach is further demonstrated by pending patents
            for predicting the efficacy of preventative measures and
            recommending treatments to promote survival using advanced AI
            algorithms. These innovations empower organizations with
            unparalleled predictive capabilities, driving transformative
            outcomes in healthcare and beyond.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics’ solutions be customized for specific industries?"
          >
            Yes, we tailor{' '}
            <Link to="/our-approach/our-capabilities">our capabilities</Link> to
            diverse industries, including{' '}
            <Link to="/industries/government/public-health-and-policy">
              government public health
            </Link>
            , ensuring practical applications for unique operational needs.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ capabilities?"
          >
            <Link to="/contact-us">Contact us</Link> today to learn how our
            solutions align with your goals.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
